/**
 * What an event of the staff stream makes stale in the money section.
 *
 * hg-realtime tells the money desk of a wallet that moved (`wallet.changed`):
 * a settled visit, a top-up, an adjustment, a payout held, given back or
 * sent. It says whose wallets moved and nothing of them, at most once a
 * second for all of them; the screens read their lists from hg-billing
 * again. A wallet that moved while the stream was away is not sent again,
 * so a `connected` that is a return (`reconnected`) reads the money afresh
 * too. The first of a page is not: the screens have just read it, and a
 * stream that opened late says `resync`, which reads everything.
 *
 * It also tells of an adjustment above its maker's limit that began to wait
 * for a second person (`adjustment.requested`) and of its decision
 * (`adjustment.decided`: approved and posted, rejected by somebody else, or
 * withdrawn by its maker). Each comes as it happens, by its ids and `state`:
 * the queue's count (the "waiting for a second person" tile and tab), the
 * lists of adjustments and the one adjustment are read again. One that was
 * posted moves its wallet too, which `wallet.changed` says.
 */
import { textOf, type LiveEvent, type Refresh } from "@/shared/live";

import {
  ADJUSTMENTS_KEY,
  adjustmentKey,
  entriesAboutKey,
  MONEY_KEY,
  OVERVIEW_KEY,
} from "./queries";

/** At most once in this long, whatever a burst of wallets says. */
export const MONEY_REFRESH_MS = 2_000;

export function moneyLive(event: LiveEvent, refresh: Refresh): void {
  if (event.type === "adjustment.requested" || event.type === "adjustment.decided") {
    refresh(OVERVIEW_KEY);
    refresh(ADJUSTMENTS_KEY);
    const adjustmentId = textOf(event, "adjustment_id");
    if (adjustmentId === null) return;
    refresh(adjustmentKey(adjustmentId));
    // Approved, it wrote its entry.
    if (event.type === "adjustment.decided") refresh(entriesAboutKey("adjustment", adjustmentId));
    return;
  }
  const returned = event.type === "connected" && event.reconnected === true;
  if (event.type !== "wallet.changed" && !returned) return;
  refresh(MONEY_KEY, MONEY_REFRESH_MS);
}
